import React, { useRef } from 'react';
import { MentionMirror } from './MentionText';
import useMentionAutocomplete from '../utils/useMentionAutocomplete';

/**
 * A textarea with @-mention autocomplete + live cyan highlighting, matching the
 * signal composer's behaviour so a tag works the same wherever you type one.
 * docs/plans/2026-08-club-forum/PLAN.md
 *
 * Props:
 *   value, onChange(text)         — controlled text
 *   people: [{ userId, displayName, handle, avatarUrl }] — who can be tagged
 *   onTag({ token, userId })      — called when someone is picked, so the caller
 *                                   can send explicit ids for handle-less people
 *   maxLength, rows, placeholder, autoFocus, className
 */
export default function MentionTextarea({
  value,
  onChange,
  people = [],
  onTag,
  maxLength = 4000,
  rows = 3,
  placeholder = '',
  autoFocus = false,
  className = '',
  singleLine = false,
}) {
  const ref = useRef(null);
  const backdropRef = useRef(null);

  // Detection + token replacement are shared with the message composer, which
  // needs the same behaviour behind completely different chrome.
  const { mention, matches, sync: syncMention, insert: insertMention, close } =
    useMentionAutocomplete({ value, onChange, people, maxLength, onTag });

  const handleChange = (e) => {
    const val = e.target.value.slice(0, maxLength);
    onChange(val);
    syncMention(val, e.target.selectionStart);
  };
  const sync = (e) => syncMention(e.target.value, e.target.selectionStart);

  const insert = (person) => {
    const res = insertMention(person);
    if (!res) return;
    // Put the caret after the inserted token.
    requestAnimationFrame(() => {
      if (!ref.current) return;
      ref.current.focus();
      ref.current.setSelectionRange(res.caret, res.caret);
    });
  };

  const shared = {
    ref,
    value,
    onChange: handleChange,
    onKeyUp: sync,
    onClick: sync,
    onBlur: () => setTimeout(close, 120),
    placeholder, // stays on the field — its gray ::placeholder shows through the transparent text
    autoFocus,
    maxLength,
  };

  // Transparent-text field over a highlighted mirror — you see the mirror (cyan
  // mentions included), the field just carries the caret + selection.
  const fieldClass = `relative w-full border-0 bg-transparent p-3 text-[15px] outline-none focus:ring-0 ${singleLine ? '' : 'resize-none leading-snug'}`;
  const mirrorClass = `pointer-events-none absolute inset-0 overflow-hidden p-3 text-[15px] text-black ${singleLine ? 'whitespace-pre' : 'whitespace-pre-wrap break-words leading-snug'}`;
  const fieldStyle = { color: 'transparent', caretColor: '#000' };

  return (
    <div className="relative">
      <div className={`relative border-2 border-black bg-white ${className}`}>
        <div ref={backdropRef} aria-hidden className={mirrorClass}>
          {value ? <MentionMirror text={value} /> : '​'}
        </div>
        {singleLine ? (
          <input
            type="text"
            {...shared}
            onScroll={(e) => { if (backdropRef.current) backdropRef.current.scrollLeft = e.target.scrollLeft; }}
            className={fieldClass}
            style={fieldStyle}
          />
        ) : (
          <textarea
            {...shared}
            rows={rows}
            onScroll={(e) => { if (backdropRef.current) backdropRef.current.scrollTop = e.target.scrollTop; }}
            className={fieldClass}
            style={fieldStyle}
          />
        )}
      </div>

      {mention && matches.length > 0 && (
        <div className="absolute inset-x-0 top-full z-30 mt-1 max-h-56 overflow-y-auto border-2 border-black bg-white shadow-hard">
          {matches.map((p) => (
            <button
              key={p.userId}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); insert(p); }}
              className="flex w-full items-center gap-3 border-b border-gray-100 px-3 py-2 text-left last:border-b-0 hover:bg-gray-50"
            >
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden border-2 border-black bg-gray-100 text-[11px] font-bold">
                {p.avatarUrl
                  ? <img src={p.avatarUrl} alt="" className="h-full w-full object-cover" />
                  : (p.displayName || '?').charAt(0).toUpperCase()}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-[13px] font-semibold">{p.displayName}</span>
                <span className="block truncate font-mono text-[10px] text-gray-500">
                  {p.handle ? `@${p.handle}` : 'no @handle yet'}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
